import { Title, Text, Blockquote, Divider, Table } from '@mantine/core';
import cn from 'classnames';
import * as React from 'react';
import { MarkdownHooks as ReactMarkdown } from 'react-markdown';
import type { ExtraProps } from 'react-markdown';
import { useParams } from 'react-router-dom';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';

import { isEmptyCodeBlock, isEmptyMarkdownBlock } from '@/utils/isEmptyMarkdownBlock';

import rehypeMermaid from './plugins/rehypeMermaid';
import createRemarkDocumentLinks from './plugins/remarkDocumentLinks';

import s from './MarkdownRenderer.module.scss';

export type MarkdownRendererProps = {
  content?: string;
  className?: string;
  paragraphClassName?: string;
  paddingBottom?: boolean;
};

type MarkdownCodeProps = React.ComponentPropsWithoutRef<'code'> &
  ExtraProps & {
    inline?: boolean;
  };

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  className,
  paragraphClassName,
  paddingBottom,
}) => {
  const { id: organizationIdFromRoute } = useParams();

  const documentLinksPlugin = React.useMemo(
    () => createRemarkDocumentLinks({ organizationId: organizationIdFromRoute }),
    [organizationIdFromRoute],
  );

  return (
    <div className={cn(s.root, paddingBottom && s.root_paddingBottom, className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, documentLinksPlugin]}
        rehypePlugins={[rehypeMermaid, rehypeRaw]}
        components={{
          // Заголовки
          h1: ({ children, ...props }) => (
            <Title order={1} className={s.root__h1} {...props}>
              {children}
            </Title>
          ),
          h2: ({ children, ...props }) => (
            <Title order={2} className={s.root__h2} {...props}>
              {children}
            </Title>
          ),
          h3: ({ children, ...props }) => (
            <Title order={3} className={s.root__h3} {...props}>
              {children}
            </Title>
          ),
          h4: ({ children, ...props }) => (
            <Title order={4} className={s.root__h4} {...props}>
              {children}
            </Title>
          ),
          h5: ({ children, ...props }) => (
            <Title order={5} className={s.root__h5} {...props}>
              {children}
            </Title>
          ),
          h6: ({ children, ...props }) => (
            <Title order={6} className={s.root__h6} {...props}>
              {children}
            </Title>
          ),

          // Параграфы
          p: ({ children }) => {
            if (isEmptyMarkdownBlock(children)) {
              return null;
            }

            return (
              <Text size="md" className={cn(s.root__paragraph, paragraphClassName)}>
                {children}
              </Text>
            );
          },

          // Форматирование текста
          strong: ({ children }) => <strong className={s.root__strong}>{children}</strong>,
          em: ({ children }) => <em className={s.root__em}>{children}</em>,
          u: ({ children }) => <u className={s.root__underline}>{children}</u>,

          // Списки
          ul: ({ children, className: ulClassName, ...props }) => (
            <ul className={cn(s.root__ul, ulClassName)} {...props}>
              {children}
            </ul>
          ),
          ol: ({ children, className: olClassName, ...props }) => (
            <ol className={cn(s.root__ol, olClassName)} {...props}>
              {children}
            </ol>
          ),
          li: ({ children, className: liClassName, ...props }) => (
            <li className={cn(s.root__li, liClassName)} {...props}>
              {children}
            </li>
          ),

          // Таблицы
          table: ({ children, ...props }) => (
            <div className={s['root__table-wrapper']} data-embla-drag-ignore="true">
              <Table className={s.root__table} {...props}>
                {children}
              </Table>
            </div>
          ),
          thead: ({ children, ...props }) => (
            <Table.Thead className={s.root__thead} {...props}>
              {children}
            </Table.Thead>
          ),
          tbody: ({ children, ...props }) => (
            <Table.Tbody className={s.root__tbody} {...props}>
              {children}
            </Table.Tbody>
          ),
          tr: ({ children, ...props }) => (
            <Table.Tr className={s.root__tr} {...props}>
              {children}
            </Table.Tr>
          ),
          th: ({ children, ...props }) => (
            <Table.Th className={s.root__th} {...props}>
              {children}
            </Table.Th>
          ),
          td: ({ children, ...props }) => (
            <Table.Td className={s.root__td} {...props}>
              {children}
            </Table.Td>
          ),

          // Ссылки
          a: ({ children, href, target, rel: relAttr, ...anchorProps }) => {
            if (!href) {
              return <>{children}</>;
            }

            const isAnchorLink = href.startsWith('#');
            const newTab = target === '_blank' || !isAnchorLink;
            const rel = newTab
              ? Array.from(new Set([...(relAttr ? relAttr.split(/\s+/) : []), 'noopener', 'noreferrer']))
                  .filter(Boolean)
                  .join(' ') || undefined
              : relAttr;

            return (
              <a
                href={href}
                className={cn(s.root__link, newTab && s.root__link_footnote)}
                target={newTab ? '_blank' : target}
                rel={rel}
                {...anchorProps}
              >
                {children}
              </a>
            );
          },
          sup: ({ children, ...props }) => (
            <sup className={s.root__sup} {...props}>
              {children}
            </sup>
          ),

          // Блоки кода
          pre: ({ children }) => {
            if (isEmptyCodeBlock(children)) {
              return null;
            }

            return <div className={s.root__pre}>{children}</div>;
          },
          code: ({ className: codeClassName, children, ...codeProps }: MarkdownCodeProps) => (
            <code className={codeClassName} {...codeProps}>
              {children}
            </code>
          ),

          // Блоки цитат
          blockquote: ({ children }) => <Blockquote className={s.root__blockquote}>{children}</Blockquote>,

          // Горизонтальные линии
          hr: () => <Divider className={s.root__hr} />,

          div: ({ children, node }) => {
            const isMermaidBlock = Boolean(node?.properties?.['data-mermaid']);

            if (isMermaidBlock) {
              return <div className={s.root__mermaid}>{children}</div>;
            }

            if (isEmptyMarkdownBlock(children)) {
              return null;
            }

            return <div className={s.root__div}>{children}</div>;
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default React.memo(MarkdownRenderer);
